


import { Component, Vue } from "vue-property-decorator";

import { CustomNotification } from "@Internal/Models";

import { Profile } from "App";
import { TotpChallenge, Session } from "App/Contracts";
import { $t, EventBus } from "App/IOC";

type Step = "idle" | "setup" | "recovery";

@Component
export default class ProfileAuthentication extends Vue {
    private loading: boolean = true;
    private enabled: boolean = false;

    private step: Step = "idle";
    private challenge?: TotpChallenge;
    private code: string = "";
    private recoveryCodes: string[] = [];
    private error: string = "";
    private busy: boolean = false;

    private sessions: Session[] = [];
    private sessionsLoading: boolean = true;

    private get canConfirm(): boolean {
        return this.code.trim().length >= 6 && !this.busy;
    }

    private async created(): Promise<void> {
        await this.loadTotp();
        await this.loadSessions();
    }

    private async loadTotp(): Promise<void> {
        const [ enabled, error ] = await Profile.TotpStatus();

        /* Drop the spinner even on error so the card renders (2FA shown off) instead of a blank
           page; the error is surfaced separately. */
        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.enabled = enabled;
    }

    private async loadSessions(): Promise<void> {
        const [ sessions, error ] = await Profile.Sessions();

        this.sessionsLoading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.sessions = sessions;
    }

    /* Render an ISO timestamp in the user's locale; empty when absent. */
    private formatSeen( iso: string ): string {
        if( iso === "" )
            return "";

        const date = new Date( iso );

        return isNaN( date.getTime() ) ? "" : date.toLocaleString();
    }

    private signOutSession( session: Session ): void {
        EventBus.Publish( "Confirmation", {
            Props: {
                Message: $t( "AUTH_sessionsSignOutConfirm" ),
                Actions: [{
                    Id: "sign-out",
                    Name: $t( "AUTH_sessionsSignOut" ),
                    Active: true
                }]
            },
            Events: { Action: () => this.runSignOut( session ) }
        });
    }

    private async runSignOut( session: Session ): Promise<void> {
        const error = await Profile.RevokeSession( session.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.sessions = this.sessions.filter( item => item.Id !== session.Id );
        this.notify( $t( "AUTH_sessionsSignedOut" ) );
    }

    /* Start enrollment: fetch the setup key, then ask for the first code. */
    private async beginSetup(): Promise<void> {
        this.error = "";
        this.busy = true;

        const [ challenge, error ] = await Profile.TotpBegin();

        this.busy = false;

        if( error !== undefined || challenge === undefined ) {
            this.error = error !== undefined ? error.Message : "GLOBAL_internalError";
            return;
        }

        this.challenge = challenge;
        this.code = "";
        this.step = "setup";
    }

    private cancelSetup(): void {
        this.step = "idle";
        this.challenge = undefined;
        this.code = "";
        this.error = "";
    }

    private async confirmSetup(): Promise<void> {
        if( !this.canConfirm )
            return;

        this.error = "";
        this.busy = true;

        const [ codes, error ] = await Profile.TotpEnable( this.code.trim() );

        this.busy = false;

        if( error !== undefined || codes === undefined ) {
            this.error = error !== undefined ? error.Message : "GLOBAL_internalError";
            return;
        }

        this.enabled = true;
        this.recoveryCodes = codes;
        this.step = "recovery";
        this.notify( $t( "AUTH_2faEnabled" ) );
    }

    private finishRecovery(): void {
        this.step = "idle";
        this.recoveryCodes = [];
        this.challenge = undefined;
        this.code = "";
    }

    private disable(): void {
        EventBus.Publish( "Confirmation", {
            Props: {
                Message: $t( "AUTH_2faDisableConfirm" ),
                Actions: [{
                    Id: "disable",
                    Name: $t( "AUTH_2faDisable" ),
                    Active: true
                }]
            },
            Events: { Action: () => this.runDisable() }
        });
    }

    private async runDisable(): Promise<void> {
        this.busy = true;

        const error = await Profile.TotpDisable();

        this.busy = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.enabled = false;
        this.notify( $t( "AUTH_2faDisabled" ) );
    }

    private async regenerate(): Promise<void> {
        this.busy = true;

        const [ codes, error ] = await Profile.TotpRecovery();

        this.busy = false;

        if( error !== undefined || codes === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.recoveryCodes = codes;
        this.step = "recovery";
    }

    private copyRecoveryCodes(): void {
        const text = this.recoveryCodes.join( "\n" );

        if( navigator.clipboard !== undefined )
            navigator.clipboard.writeText( text );

        this.notify( $t( "AUTH_recoveryCopied" ) );
    }

    private notify( message: string ): void {
        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "PROFILE_navAuthentication" ), message
        ));
    }
}
